import React, { useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import ColorPill from 'core-system/Pill/ColorPill'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Avatar from 'core-system/Avatar/Avatar'
import {
  mockKpisByTimeframe,
  mockAgencies,
  mockPipelineStages,
  mockEmployees,
  mockAuditLog,
} from './transitPassMockData'
import { TimeframeOption, CardStatus, ExceptionType } from './transitPassTypes'

// ─── Layout ────────────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 75rem;
  width: 100%;
`

const Card = styled.div`
  ${(props) => props.theme.baseCard};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const Spacer = styled.div`
  flex: 1;
`

// ─── Page header ──────────────────────────────────────────────────────────────

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const TimeframeGroup = styled.div`
  display: flex;
  background-color: ${(props) => props.theme.palette.grey.grey100};
  border-radius: 0.5rem;
  padding: 0.25rem;
  gap: 0.125rem;
`

const TimeframeBtn = styled.button<{ active: boolean }>`
  padding: 0.375rem 0.875rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  background-color: ${(props) =>
    props.active ? props.theme.palette.white : 'transparent'};
  color: ${(props) =>
    props.active
      ? props.theme.palette.text.primary
      : props.theme.palette.text.secondary};
  box-shadow: ${(props) =>
    props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  transition: all 0.15s;
`

const AutoUploadToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.success.green50};
  border: 1px solid ${(props) => props.theme.palette.success.green200};
  cursor: pointer;
  user-select: none;
`

const ToggleTrack = styled.div<{ on: boolean }>`
  width: 2rem;
  height: 1.125rem;
  border-radius: 999px;
  background-color: ${(props) =>
    props.on
      ? props.theme.palette.success.green500
      : props.theme.palette.grey.grey300};
  position: relative;
  flex-shrink: 0;
  transition: background-color 0.2s;
`

const ToggleThumb = styled.div<{ on: boolean }>`
  position: absolute;
  top: 0.1875rem;
  left: ${(props) => (props.on ? '1rem' : '0.1875rem')};
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background: ${(props) => props.theme.palette.white};
  transition: left 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey200};
`

const Tab = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.25rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${(props) => (props.active ? 600 : 400)};
  color: ${(props) =>
    props.active
      ? props.theme.palette.primary.pink800
      : props.theme.palette.text.secondary};
  border-bottom: 2px solid
    ${(props) =>
      props.active ? props.theme.palette.primary.pink800 : 'transparent'};
  margin-bottom: -1px;
  transition: all 0.15s;

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
  }
`

const ExceptionBadge = styled.span`
  margin-left: 0.375rem;
  background: ${(props) => props.theme.palette.error.red500};
  color: ${(props) => props.theme.palette.white};
  border-radius: 999px;
  padding: 0.0625rem 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
`

// ─── KPI strip ────────────────────────────────────────────────────────────────

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const KpiCard = styled.div<{ accent: string }>`
  ${(props) => props.theme.baseCard};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border-top: 3px solid
    ${(props) => {
      if (props.accent === 'green') return props.theme.palette.success.green400
      if (props.accent === 'red') return props.theme.palette.error.red400
      if (props.accent === 'blue') return props.theme.palette.accent.blue400
      return props.theme.palette.grey.grey300
    }};
`

const KpiValue = styled.div`
  font-size: 1.875rem;
  font-weight: 700;
  line-height: 1.1;
  color: ${(props) => props.theme.palette.text.primary};
`

const KpiLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(props) => props.theme.palette.text.secondary};
  margin-top: 0.125rem;
`

const KpiSub = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.placeholder};
`

const TrendBadge = styled.span<{ positive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  margin-top: 0.375rem;
  background-color: ${(props) =>
    props.positive
      ? props.theme.palette.success.green100
      : props.theme.palette.error.red100};
  color: ${(props) =>
    props.positive
      ? props.theme.palette.success.green700
      : props.theme.palette.error.red700};
`

// ─── Pipeline ─────────────────────────────────────────────────────────────────

const PipelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const PipelineCard = styled.div<{ accent: string }>`
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  border-radius: 0.75rem;
  background: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey.grey200};
  border-top: 3px solid ${(props) => props.accent};
  position: relative;
`

const PipelineCount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
  color: ${(props) => props.theme.palette.text.primary};
`

const PipelineLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.theme.palette.text.primary};
  margin-top: 0.5rem;
`

const PipelineDesc = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
  margin-top: 0.25rem;
  flex: 1;
`

const PipelineArrow = styled.div`
  position: absolute;
  right: -0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  background: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey.grey200};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  font-size: 0.625rem;
  color: ${(props) => props.theme.palette.text.secondary};

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    display: none;
  }
`

const PipelineEmployeeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey100};
`

const FormatBadge = styled.span<{ isNextGen: boolean }>`
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  background: ${(props) =>
    props.isNextGen
      ? props.theme.palette.accent.blue100
      : props.theme.palette.primary.pink100};
  color: ${(props) =>
    props.isNextGen
      ? props.theme.palette.accent.blue700
      : props.theme.palette.primary.pink700};
  white-space: nowrap;
`

// ─── Agency cards ─────────────────────────────────────────────────────────────

const AgencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }
`

const AgencyLogoImg = styled.img`
  height: 2rem;
  width: auto;
  max-width: 7rem;
  object-fit: contain;
  flex-shrink: 0;
`

const AgencyCard = styled.div<{ borderColor: string }>`
  ${(props) => props.theme.baseCard};
  border-left: 4px solid ${(props) => props.borderColor};
`

const AgencyHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const AgencyName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${(props) => props.theme.palette.text.primary};
`

const AgencyRegion = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
  margin-top: 0.125rem;
`

const AgencyStatLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
`

const AgencyStatLabel = styled.span`
  font-size: 0.8125rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

const AgencyStatValue = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${(props) => props.theme.palette.text.primary};
`

const ActivationBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${(props) => props.theme.palette.grey.grey100};
  border-radius: 3px;
  margin-top: 0.75rem;
  overflow: hidden;
`

const ActivationFill = styled.div<{ pct: number; color: string }>`
  height: 100%;
  width: ${(props) => props.pct}%;
  background: ${(props) => props.color};
  border-radius: 3px;
`

// ─── Exception queue ──────────────────────────────────────────────────────────

const ExceptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey100};

  &:last-child {
    border-bottom: none;
  }
`

const ExceptionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
`

const ExceptionName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.theme.palette.text.primary};
`

const ExceptionMeta = styled.span`
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

// ─── Table ────────────────────────────────────────────────────────────────────

const TableOperationsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

const SearchInput = styled.input`
  padding: 0.5rem 0.875rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  width: 16rem;
  outline: none;
  background: ${(props) => props.theme.palette.white};
  color: ${(props) => props.theme.palette.text.primary};

  &:focus {
    border-color: ${(props) => props.theme.palette.primary.pink400};
  }

  &::placeholder {
    color: ${(props) => props.theme.palette.text.placeholder};
  }
`

const CardMonospace = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 0.8125rem;
`

const DaysElapsed = styled.span<{ warn: boolean }>`
  font-weight: 600;
  color: ${(props) =>
    props.warn ? props.theme.palette.error.red500 : 'inherit'};
`

// ─── Audit log ────────────────────────────────────────────────────────────────

const AuditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey100};

  &:last-child {
    border-bottom: none;
  }
`

const AuditDot = styled.div<{ status: 'success' | 'manually_completed' | 'failed' }>`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => {
    if (props.status === 'success') return props.theme.palette.success.green500
    if (props.status === 'manually_completed') return props.theme.palette.accent.blue400
    return props.theme.palette.error.red400
  }};
`

const AuditText = styled.span`
  font-size: 0.8125rem;
  color: ${(props) => props.theme.palette.text.primary};
  flex: 1;
`

const AuditMeta = styled.span`
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
  white-space: nowrap;
`

// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusConfig: Record<
  CardStatus,
  { label: string; color: 'green' | 'blue' | 'yellow' | 'red' | 'gray' }
> = {
  active:     { label: 'Active',     color: 'green'  },
  uploaded:   { label: 'Uploaded',   color: 'blue'   },
  processing: { label: 'Processing', color: 'yellow' },
  submitted:  { label: 'Submitted',  color: 'gray'   },
  exception:  { label: 'Exception',  color: 'red'    },
}

const exceptionConfig: Record<ExceptionType, { label: string; action: string }> = {
  transposed_digits:    { label: 'Possible transposed digits', action: 'Ask employee to re-submit' },
  approaching_deadline: { label: 'Approaching 10-day window',  action: 'Follow up with employee'  },
  failed_upload:        { label: 'Failed portal upload',       action: 'Retry upload'             },
  inactive_employee:    { label: 'Employee left roster',       action: 'Offboard from BayPass'    },
}

const HeaderCell = (text: string) => (
  <TableRowCell>
    <Text fontWeight={600} variant='action4'>{text}</Text>
  </TableRowCell>
)

// ─── Component ────────────────────────────────────────────────────────────────

type TabKey = 'overview' | 'pipeline' | 'employees' | 'settings'

const TransitPassAdminView: React.FC = () => {
  const [timeframe, setTimeframe] = useState<TimeframeOption>('30d')
  const [tab, setTab] = useState<TabKey>('overview')
  const [autoUpload, setAutoUpload] = useState(true)
  const [search, setSearch] = useState('')

  const kpis = mockKpisByTimeframe[timeframe]
  const exceptions = mockEmployees.filter((e) => e.status === 'exception')
  const filtered = mockEmployees.filter(
    (e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.site.toLowerCase().includes(search.toLowerCase())
  )

  const headerCells = [
    HeaderCell('Employee'),
    HeaderCell('Site'),
    HeaderCell('Card (masked)'),
    HeaderCell('Format'),
    HeaderCell('Portal'),
    HeaderCell('Submitted'),
    HeaderCell('Days'),
    HeaderCell('Status'),
  ]

  const tableRows = filtered.map((emp) => {
    const sc = statusConfig[emp.status]
    return (
      <TableRow key={emp.id}>
        <TableRowCell>
          <Row>
            <Avatar name={emp.name} size='small' />
            <Text variant='body2' style={{ fontWeight: 500 }}>{emp.name}</Text>
          </Row>
        </TableRowCell>
        <TableRowCell>
          <Text variant='body2' style={{ opacity: 0.7 }}>{emp.site}</Text>
        </TableRowCell>
        <TableRowCell noWrap>
          <CardMonospace>{emp.cardNumberMasked}</CardMonospace>
        </TableRowCell>
        <TableRowCell>
          <FormatBadge isNextGen={emp.cardFormat === '19-digit'}>
            {emp.cardFormat}
          </FormatBadge>
        </TableRowCell>
        <TableRowCell>
          <Text variant='body2' style={{ opacity: 0.75 }}>{emp.portal}</Text>
        </TableRowCell>
        <TableRowCell noWrap>
          <Text variant='body2' style={{ opacity: 0.75 }}>{emp.submittedDate}</Text>
        </TableRowCell>
        <TableRowCell>
          <DaysElapsed warn={emp.daysElapsed >= 9 && emp.status !== 'active'}>
            {emp.daysElapsed}d
          </DaysElapsed>
        </TableRowCell>
        <TableRowCell>
          <ColorPill color={sc.color} text={sc.label} />
          {emp.exceptionType && (
            <Text variant='body2' style={{ fontSize: '0.6875rem', opacity: 0.65, marginTop: '0.25rem' }}>
              {exceptionConfig[emp.exceptionType].label}
            </Text>
          )}
        </TableRowCell>
      </TableRow>
    )
  })

  return (
    <PageContainer>
      {/* ── Page header ── */}
      <HeaderRow>
        <HeaderLeft>
          <Text variant='h5'>Transit Pass</Text>
          <Text variant='body2' style={{ opacity: 0.6 }}>
            Manage Clipper card enrollment, portal uploads, and activation across all agencies
          </Text>
        </HeaderLeft>
        <Spacer />
        <AutoUploadToggleRow onClick={() => setAutoUpload((v) => !v)}>
          <ToggleTrack on={autoUpload}>
            <ToggleThumb on={autoUpload} />
          </ToggleTrack>
          <Text variant='body2' style={{ fontWeight: 600, fontSize: '0.8125rem' }}>
            Auto-upload {autoUpload ? 'ON' : 'OFF'}
          </Text>
          <Text variant='body2' style={{ fontSize: '0.75rem', opacity: 0.65 }}>
            · nightly at 11 PM
          </Text>
        </AutoUploadToggleRow>
        <TimeframeGroup>
          {(['7d', '30d', '90d'] as TimeframeOption[]).map((t) => (
            <TimeframeBtn key={t} active={timeframe === t} onClick={() => setTimeframe(t)}>
              {t}
            </TimeframeBtn>
          ))}
        </TimeframeGroup>
      </HeaderRow>

      {/* ── KPI strip ── */}
      <KpiGrid>
        {kpis.map((kpi) => (
          <KpiCard key={kpi.label} accent={kpi.accent ?? 'neutral'}>
            <KpiLabel>{kpi.label}</KpiLabel>
            <KpiValue>{kpi.value}</KpiValue>
            <KpiSub>{kpi.sub}</KpiSub>
            {kpi.trend !== undefined && (
              <TrendBadge positive={kpi.trend >= 0}>
                {kpi.trend >= 0 ? '↑' : '↓'} {kpi.trendLabel}
              </TrendBadge>
            )}
          </KpiCard>
        ))}
      </KpiGrid>

      {/* ── Tabs ── */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <TabBar>
          {([
            ['overview',  'Overview'],
            ['pipeline',  'Pipeline'],
            ['employees', 'Employees'],
            ['settings',  'Settings'],
          ] as [TabKey, string][]).map(([key, label]) => (
            <Tab key={key} active={tab === key} onClick={() => setTab(key)}>
              {label}
              {key === 'pipeline' && (
                <span style={{ marginLeft: '0.375rem', fontSize: '0.6875rem', opacity: 0.7 }}>
                  ({mockPipelineStages.reduce((s, p) => s + (p.key !== 'active' ? p.count : 0), 0)})
                </span>
              )}
              {key === 'employees' && exceptions.length > 0 && tab !== key && (
                <ExceptionBadge>{exceptions.length}</ExceptionBadge>
              )}
            </Tab>
          ))}
        </TabBar>

        <div style={{ padding: '1.5rem' }}>

          {/* ══ OVERVIEW ══════════════════════════════════════════════════════ */}
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <Text variant='body1' style={{ fontWeight: 600, marginBottom: '1rem' }}>
                  By Agency
                </Text>
                <AgencyGrid>
                  {mockAgencies.map((agency) => (
                    <AgencyCard key={agency.id} borderColor={agency.color}>
                      <AgencyHeader>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                          <AgencyLogoImg src={agency.logoUrl} alt={agency.name} />
                          <AgencyRegion>{agency.region}</AgencyRegion>
                        </div>
                        <TrendBadge positive={agency.trendValue >= 0}>
                          {agency.trendValue >= 0 ? '↑' : '↓'} {agency.trendLabel}
                        </TrendBadge>
                      </AgencyHeader>

                      {[
                        { label: 'Enrolled',       value: agency.enrolled },
                        { label: 'Active cards',    value: agency.active },
                        { label: 'Pending',         value: agency.pending },
                        { label: 'Monthly spend',   value: `$${agency.monthlySpend.toLocaleString()}` },
                      ].map(({ label, value }) => (
                        <AgencyStatLine key={label}>
                          <AgencyStatLabel>{label}</AgencyStatLabel>
                          <AgencyStatValue>{value}</AgencyStatValue>
                        </AgencyStatLine>
                      ))}

                      <div style={{ marginTop: '0.875rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <AgencyStatLabel>Activation rate</AgencyStatLabel>
                          <AgencyStatValue style={{ color: agency.color }}>{agency.activationRate}%</AgencyStatValue>
                        </div>
                        <ActivationBar>
                          <ActivationFill pct={agency.activationRate} color={agency.color} />
                        </ActivationBar>
                      </div>
                    </AgencyCard>
                  ))}
                </AgencyGrid>
              </div>

              {exceptions.length > 0 && (
                <div>
                  <Row style={{ marginBottom: '1rem' }}>
                    <Text variant='body1' style={{ fontWeight: 600 }}>Exception Queue</Text>
                    <ColorPill color='red' text={`${exceptions.length} items`} />
                    <Spacer />
                    <SecondaryButton size='small' onClick={() => setTab('employees')}>
                      View all employees →
                    </SecondaryButton>
                  </Row>
                  {exceptions.map((emp) => {
                    const exc = exceptionConfig[emp.exceptionType!]
                    return (
                      <ExceptionRow key={emp.id}>
                        <Avatar name={emp.name} size='small' />
                        <ExceptionInfo>
                          <ExceptionName>{emp.name}</ExceptionName>
                          <ExceptionMeta>
                            {emp.site} · {emp.cardNumberMasked} · {emp.cardFormat} · {emp.daysElapsed}d elapsed
                          </ExceptionMeta>
                          <ExceptionMeta style={{ marginTop: '0.125rem' }}>
                            <strong>{exc.label}</strong> — {exc.action}
                          </ExceptionMeta>
                        </ExceptionInfo>
                        <PrimaryButton size='small'>
                          {emp.exceptionType === 'inactive_employee' ? 'Offboard' : 'Fix'}
                        </PrimaryButton>
                      </ExceptionRow>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ══ PIPELINE ══════════════════════════════════════════════════════ */}
          {tab === 'pipeline' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <PipelineGrid>
                {mockPipelineStages.map((stage, idx) => (
                  <PipelineCard key={stage.key} accent={stage.color}>
                    {idx < mockPipelineStages.length - 1 && (
                      <PipelineArrow>›</PipelineArrow>
                    )}
                    <PipelineCount>{stage.count}</PipelineCount>
                    <PipelineLabel>{stage.label}</PipelineLabel>
                    <PipelineDesc>{stage.description}</PipelineDesc>

                    {stage.key !== 'active' && (
                      <div style={{ marginTop: '1rem' }}>
                        {mockEmployees
                          .filter((e) => e.status === stage.key)
                          .slice(0, 3)
                          .map((e) => (
                            <PipelineEmployeeRow key={e.id}>
                              <Avatar name={e.name} size='small' />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Text variant='body2' style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {e.name}
                                </Text>
                                <Text variant='body2' style={{ fontSize: '0.6875rem', opacity: 0.6 }}>
                                  Day {e.daysElapsed}
                                </Text>
                              </div>
                              <FormatBadge isNextGen={e.cardFormat === '19-digit'}>
                                {e.portal}
                              </FormatBadge>
                            </PipelineEmployeeRow>
                          ))}
                        {mockEmployees.filter((e) => e.status === stage.key).length > 3 && (
                          <Text variant='body2' style={{ fontSize: '0.75rem', opacity: 0.55, marginTop: '0.375rem' }}>
                            +{mockEmployees.filter((e) => e.status === stage.key).length - 3} more
                          </Text>
                        )}
                      </div>
                    )}
                  </PipelineCard>
                ))}
              </PipelineGrid>

              <div style={{
                padding: '1rem',
                borderRadius: '0.5rem',
                background: '#FEF9C3',
                border: '1px solid #FDE047',
                display: 'flex',
                gap: '0.75rem',
              }}>
                <span style={{ fontSize: '1.125rem' }}>⏱</span>
                <div>
                  <Text variant='body2' style={{ fontWeight: 600, color: '#713F12' }}>
                    10-day activation window
                  </Text>
                  <Text variant='body2' style={{ color: '#92400E', marginTop: '0.25rem' }}>
                    Cards purchased at Walgreens, CVS, or a TVM that aren&apos;t activated within 10 days
                    may route to the NextGen portal instead of Classic. Fleet flags these automatically.
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* ══ EMPLOYEES ═════════════════════════════════════════════════════ */}
          {tab === 'employees' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <TableOperationsRow>
                <SearchInput
                  placeholder='Search by name or site…'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Spacer />
                <Text variant='body2' style={{ opacity: 0.55 }}>
                  {filtered.length} employee{filtered.length !== 1 ? 's' : ''}
                </Text>
                <PrimaryButton size='small'>Export CSV</PrimaryButton>
              </TableOperationsRow>

              <Table
                headerCells={headerCells}
                rows={tableRows}
                maxRows={10}
              />
            </div>
          )}

          {/* ══ SETTINGS ══════════════════════════════════════════════════════ */}
          {tab === 'settings' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <Text variant='body1' style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  Auto-upload
                </Text>
                <Text variant='body2' style={{ opacity: 0.65, marginBottom: '1rem' }}>
                  Fleet uses an admin account on MTC to automatically push new enrollments to the
                  correct Clipper portal each night. No manual CSV downloads or portal juggling needed.
                </Text>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: `1px solid ${autoUpload ? '#BBF7D0' : '#E5E7EB'}`,
                  background: autoUpload ? '#F0FDF4' : '#F9FAFB',
                }}>
                  <ToggleTrack on={autoUpload} onClick={() => setAutoUpload((v) => !v)} style={{ cursor: 'pointer' }}>
                    <ToggleThumb on={autoUpload} />
                  </ToggleTrack>
                  <div>
                    <Text variant='body1' style={{ fontWeight: 600 }}>
                      Nightly auto-upload — {autoUpload ? 'Enabled' : 'Disabled'}
                    </Text>
                    <Text variant='body2' style={{ opacity: 0.65 }}>
                      Runs at 11:00 PM PT · NextGen portal for 19-digit, Classic portal for 10-digit
                    </Text>
                  </div>
                </div>
              </div>

              <div>
                <Text variant='body1' style={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Upload audit log
                </Text>
                {mockAuditLog.map((entry) => {
                  const auditStatus = entry.status as 'success' | 'manually_completed' | 'failed'
                  const auditColor = auditStatus === 'success' ? 'green' : auditStatus === 'manually_completed' ? 'blue' : 'red'
                  const auditLabel = auditStatus === 'manually_completed' ? 'Manually completed' : auditStatus
                  return (
                    <AuditRow key={entry.id}>
                      <AuditDot status={auditStatus} />
                      <AuditText>{entry.action}</AuditText>
                      <AuditMeta style={{ marginRight: '0.5rem' }}>{entry.records} records</AuditMeta>
                      <ColorPill color={auditColor} text={auditLabel} />
                      <AuditMeta>{entry.timestamp}</AuditMeta>
                    </AuditRow>
                  )
                })}
              </div>

              <div>
                <Text variant='body1' style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  Portal routing rules
                </Text>
                <Text variant='body2' style={{ opacity: 0.65, marginBottom: '1rem' }}>
                  Fleet automatically routes each card to the correct MTC portal based on card length.
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {[
                    { format: '19-digit cards',                               isNextGen: true,  portal: 'NextGen Clipper Portal' },
                    { format: '10-digit cards (default)',                      isNextGen: false, portal: 'Classic Clipper Portal' },
                    { format: '10-digit · TVM / Walgreens / CVS · >10 days',  isNextGen: true,  portal: 'NextGen Clipper Portal (auto-detected)' },
                  ].map((rule) => (
                    <div key={rule.format} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #E5E7EB',
                      background: '#FAFAFA',
                    }}>
                      <FormatBadge isNextGen={rule.isNextGen} style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}>
                        {rule.format}
                      </FormatBadge>
                      <span style={{ opacity: 0.4 }}>→</span>
                      <Text variant='body2' style={{ fontWeight: 500 }}>{rule.portal}</Text>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Text variant='body1' style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  Employer portal URLs
                </Text>
                <Text variant='body2' style={{ opacity: 0.65, marginBottom: '1rem' }}>
                  Direct links to the MTC Clipper portals used for card enrollment uploads.
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {[
                    { label: 'NextGen Clipper Portal', url: 'nextgen.clippercard.com', isNextGen: true },
                    { label: 'Classic Clipper Portal', url: 'www.clippercard.com',     isNextGen: false },
                  ].map((portal) => (
                    <div key={portal.label} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #E5E7EB',
                      background: '#FAFAFA',
                    }}>
                      <FormatBadge isNextGen={portal.isNextGen} style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}>
                        {portal.label}
                      </FormatBadge>
                      <Text variant='body2' style={{ fontFamily: 'monospace', opacity: 0.75 }}>{portal.url}</Text>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Text variant='body1' style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  Fleet portal access
                </Text>
                <Text variant='body2' style={{ opacity: 0.65, marginBottom: '1rem' }}>
                  Fleet uses a shared admin account to authenticate with MTC portals for automated uploads.
                  Contact your Fleet rep to rotate credentials.
                </Text>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.875rem 1rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #E5E7EB',
                  background: '#FAFAFA',
                }}>
                  <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                    <circle cx='12' cy='8' r='4' stroke='#6B7280' strokeWidth='1.8' />
                    <path d='M4 20c0-4 3.582-7 8-7s8 3 8 7' stroke='#6B7280' strokeWidth='1.8' strokeLinecap='round' />
                  </svg>
                  <div>
                    <Text variant='body2' style={{ fontWeight: 600 }}>Fleet admin account</Text>
                    <Text variant='body2' style={{ fontFamily: 'monospace', opacity: 0.7, fontSize: '0.8125rem' }}>
                      transit-admin@fleetbot.ai
                    </Text>
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <ColorPill color='green' text='Active' />
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </Card>
    </PageContainer>
  )
}

export default TransitPassAdminView
